import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import IndustriesList from "../../features/manageIndustries/industriesList";
import IndustriesTopbar from "../../features/manageIndustries/topbar";
import { useEffect, useState } from "react";
import {
  useCreateIndustryMutation,
  useUpdateIndustryMutation,
  useLazyDeleteIndustryQuery,
} from "../../services/industries";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import IndustryDetails from "../../Modals/industryDetails";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";
import useIndustries from "../../hooks/useIndustries";
import type { PageChangeHandler, IndustryTableRow } from "../../types/api";
import { getErrorMessage } from "../../types/api";
import type { IndustryListItem } from "../../reducers/industriesSlice";

const ManageIndustries = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createIndustry] = useCreateIndustryMutation();
  const [updateIndustryMutation] = useUpdateIndustryMutation();
  const [deleteIndustry] = useLazyDeleteIndustryQuery();
  const [selectedId, setSelectedId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Use industries hook for data fetching and Redux management
  const {
    industries: reduxIndustriesList,
    isLoading: isIndustriesLoading,
    error: industriesError,
    addIndustryToStore,
    updateIndustryInStore,
    removeIndustryFromStore,
  } = useIndustries({ fetchOnMount: true });

  const [name, setName] = useState<string>("");
  const [nameAr, setNameAr] = useState<string>("");
  const { isAdd, isDelete } = usePermission({
    label: "Manage Industries",
    page_permission: "isView",
  });

  const [allRows, setAllRows] = useState<IndustryTableRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<IndustryTableRow[]>([]);
  const [rows, setRows] = useState<IndustryTableRow[]>([]);

  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const translate = useTranslation();

  const handlePageChange: PageChangeHandler = (event, newPage) => {
    setPage(newPage);
  };

  // Sync allRows with Redux industries list
  useEffect(() => {
    if (reduxIndustriesList.length > 0) {
      setAllRows(reduxIndustriesList);
    }
  }, [reduxIndustriesList]);

  // Show error if industries fetch failed
  useEffect(() => {
    if (industriesError) {
      showError(translate?.auth?.failedToLoadIndustries || industriesError);
    }
  }, [industriesError]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    let filtered = allRows;
    if (debouncedSearchTerm.trim()) {
      filtered = allRows.filter((row: IndustryTableRow) =>
        row?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }
    setFilteredRows(filtered);

    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = filtered.slice(startIndex, endIndex);
    setRows(paginatedRows);
  }, [allRows, debouncedSearchTerm, page]);

  const handleSubmit = async () => {
    setIsFormLoading(true);
    const body = {
      name: name,
      name_ar: nameAr,
    };

    if (selectedId) {
      try {
        const response = await updateIndustryMutation({
          body,
          id: selectedId,
        }).unwrap();
        if (response?.statusCode === 200) {
          const updated = response?.data ?? body;
          const updatedRow = { ...updated, _id: selectedId, id: selectedId };
          setAllRows((prev) =>
            prev.map((r) => (r._id === selectedId ? { ...r, ...updatedRow } : r))
          );
          updateIndustryInStore(selectedId, updatedRow);
          showToast(
            translate?.auth?.industryUpdatedSuccess ||
              "Industry updated successfully"
          );
          setOpen(false);
          resetForm();
        }
      } catch {
        showError(translate?.auth?.failToUpdate || "Failed to update industry");
      } finally {
        setIsFormLoading(false);
      }
    } else {
      try {
        const response = await createIndustry({ data: body }).unwrap();
        if (response?.statusCode === 200) {
          const created = response?.data;
          if (created) {
            const newRow: IndustryListItem = {
              ...created,
              id: created._id,
              _id: created._id,
            };
            setAllRows((prev) => [...prev, newRow]);
            addIndustryToStore(newRow);
          }
          showToast(
            translate?.auth?.industryCreatedSuccess ||
              "Industry created successfully"
          );
          setOpen(false);
          resetForm();
        }
      } catch (error: unknown) {
        showError(getErrorMessage(error, "Failed to create industry"));
      } finally {
        setIsFormLoading(false);
      }
    }
  };

  const resetForm = () => {
    setName("");
    setNameAr("");
    setSelectedId("");
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteIndustry({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(
          translate?.auth?.industryDeletedSuccess ||
            "Industry deleted successfully"
        );
        removeIndustryFromStore(selectedId);
        const remainingCount = filteredRows.length - 1;
        if (remainingCount > 0 && rows.length === 1 && page > 1) {
          const maxPage = Math.ceil(remainingCount / itemsPerPage);
          setPage(Math.min(page - 1, maxPage));
        }
        setAllRows((prev) => prev.filter((r) => r._id !== selectedId));
        setOpenDelete(false);
        setSelectedId("");
      }
    } catch {
      showError("Failed to delete industry");
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    resetForm();
  };

  const handleCloseDetailsModal = () => {
    setOpenDetails(false);
    setSelectedId("");
  };

  const handleEditClick = (row: { _id: string; name?: string; name_ar?: string }) => {
    setSelectedId(row._id);
    setName(row.name || "");
    setNameAr(row.name_ar || "");
    setOpen(true);
  };

  return (
    <MainContainer isLoading={isIndustriesLoading}>
      <div className="main_title">
        <h1>Manage Industries</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate?.dashboard?.dashboard || "Dashboard"}
          </span>{" "}
          Manage Industries
        </p>
      </div>

      <div className="cards">
        <IndustriesTopbar
          name={name}
          setName={setName}
          handleSubmit={handleSubmit}
          setOpen={setOpen}
          open={open}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSearchTerm={setSearchTerm}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          searchTerm={searchTerm}
          isFormLoading={isFormLoading}
          nameAr={nameAr}
          setNameAr={setNameAr}
        />
        <IndustriesList
          setSelectedId={setSelectedId}
          setOpen={setOpen}
          open={open}
          name={name}
          setName={setName}
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          rows={rows}
          setRows={setRows}
          openDetails={openDetails}
          setOpenDetails={setOpenDetails}
          isLoading={isLoading}
          handleEditClick={handleEditClick}
          handlePageChange={handlePageChange}
          setPage={setPage}
          page={page}
          totalCount={filteredRows.length}
          setNameAr={setNameAr}
        />
      </div>
      <ConfirmationModal
        text={translate?.auth?.confirmDeleteIndustry || "Are you sure you want to delete this industry?"}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
        isLoading={isLoading}
      />
      <IndustryDetails
        open={openDetails}
        name={name}
        onClose={handleCloseDetailsModal}
        selectedId={selectedId}
        nameAr={nameAr}
      />
    </MainContainer>
  );
};

export default ManageIndustries;
